/**
 * شاشة «الإشراف الإداري» — متابعة التوقفات، والمخالفات.
 *
 * 🔴 **الفجوة اللي الشاشة دي بتقفلها:** ١٨١ توقفًا دخلوا السيستم من
 *    الاستيراد و**مفيش أي طريق يقفل واحد منهم**. القايمة اللي بتكبر وما
 *    بتقلّش الفريق بيبطّل يبصّ لها — وده أسوأ من إنها ما تكونش موجودة.
 *
 * 🔑 **الشريحة الأولى «متابعة مستحقة» هي سبب وجود الشاشة**، مش فلتر إضافي.
 *    وتعريفها **مش** «كل النشط» (٧٢ في وش الفريق يوميًا بيتجاهلوا) — هي
 *    التوقف اللي **موعد متابعته عدّى**، أو **سايب بلا موعد ونهايته مش
 *    محددة**. يعني اللي محتاج قرار النهارده.
 *
 * ⚠️ **«طلبات المعلمين» بتوصل من تليجرام والتوكن لسه مش متحط.** الرقم
 *    بيترجع `null` مش صفر، والشاشة بتقول «القناة مش شغّالة» بدل ما توري
 *    صفرًا يخلّي المشرفة تفتكر إن مفيش طلبات.
 *
 * 🔒 الرصد والحكم صلاحيتان: اللي بيرصد المخالفة مش اللي بيقرر فيها.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Shell } from "@/components/Shell";
import {
  EmptyState, ErrorState, LoadingState, NoPermissionState,
  NoResultsState, OfflineState,
} from "@/components/states/ScreenStates";
import { Dash, DateAr } from "@/components/RegistryBits";
import { fmtNum } from "@/lib/format";
import { ApiError, errorMessageAr, OfflineError } from "@/api/client";
import {
  decideWarning, getSupervisionSummary, listSupervisionLeaves, listWarnings,
  patchLeave, proposeWarning, searchTeachers, LEAVE_SEGMENTS,
  type LeaveRow, type LeaveSegment, type LeavesMeta,
  type SupervisionSummary, type WarningRow,
} from "@/api/supervision";
import "@/styles/registry.css";

const PER_PAGE = 15;
const MIN_SEARCH = 2;
const DEBOUNCE_MS = 350;

type Tab = "leaves" | "warnings" | "requests";
const TABS: { key: Tab; label: string }[] = [
  { key: "leaves", label: "التوقفات" },
  { key: "warnings", label: "المخالفات والمكافآت" },
  { key: "requests", label: "طلبات المعلمين" },
];

const LEAVE_TYPE_AR: Record<string, string> = {
  study_pause_temp: "توقف مؤقت",
  study_pause_permanent: "توقف دائم",
  never_completed: "ما كمّلش",
  financial_hold: "إيقاف مالي",
  leave_with_teacher: "إجازة مع المعلم",
  teacher_work_leave: "إجازة عمل",
};
const SUBJ_AR: Record<string, string> = {
  student: "طالب", family: "أسرة", teacher: "معلم",
};

export function AdminSupervision() {
  const [tab, setTab] = useState<Tab>("leaves");
  const [summary, setSummary] = useState<SupervisionSummary | null>(null);
  const [denied, setDenied] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [query, setQuery] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  const deb = useRef<number | null>(null);
  useEffect(() => {
    const t = searchInput.trim();
    const next = t.length >= MIN_SEARCH ? t : "";
    if (deb.current) window.clearTimeout(deb.current);
    deb.current = window.setTimeout(() => setQuery((c) => (c === next ? c : next)), DEBOUNCE_MS);
    return () => { if (deb.current) window.clearTimeout(deb.current); };
  }, [searchInput]);

  const load = useCallback(async () => {
    try {
      setSummary(await getSupervisionSummary());
      setDenied(false);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) { setDenied(true); return; }
      console.error("[supervision] الملخّص فشل", err);
    }
  }, []);
  useEffect(() => { void load(); }, [load, reloadKey]);

  if (denied) {
    return (
      <Shell title="الإشراف الإداري">
        <NoPermissionState messageAr="الإشراف الإداري مش من صلاحيات قسمك" />
      </Shell>
    );
  }

  return (
    <Shell
      title="الإشراف الإداري"
      {...(tab === "requests" ? {} : {
        search: { value: searchInput, onChange: setSearchInput,
                  placeholder: tab === "leaves" ? "ابحث في سبب التوقف…" : "ابحث…" },
      })}
    >
      {summary ? <Cards s={summary} onTab={setTab} /> : null}

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        {TABS.map((t) => (
          <button key={t.key} type="button"
                  className={`filt${tab === t.key ? " on" : ""}`}
                  onClick={() => { setTab(t.key); setSearchInput(""); }}>
            {t.label}
            {summary && t.key === "warnings" && summary.warningsPending > 0 ? (
              <> · <span className="num">{fmtNum(summary.warningsPending)}</span></>
            ) : null}
          </button>
        ))}
      </div>

      {tab === "leaves" ? (
        <LeavesTab query={query} onClearSearch={() => setSearchInput("")}
                   onChanged={() => setReloadKey((k) => k + 1)} />
      ) : null}
      {tab === "warnings" ? (
        <WarningsTab onChanged={() => setReloadKey((k) => k + 1)} reloadKey={reloadKey} />
      ) : null}
      {tab === "requests" ? <RequestsTab s={summary} /> : null}
    </Shell>
  );
}

function Cards({ s, onTab }: { s: SupervisionSummary; onTab: (t: Tab) => void }) {
  return (
    <div className="reg-card" style={{ padding: 14 }}>
      <div style={{ display: "flex", gap: 20, flexWrap: "wrap", alignItems: "flex-start" }}>
        {/* 🔑 الرقم ده هو سبب وجود الشاشة */}
        <button type="button" className="filt on" onClick={() => onTab("leaves")}
                style={{ textAlign: "start", lineHeight: 1.7 }}>
          <div className="t-meta">توقفات مستحقة المتابعة</div>
          <div style={{ fontSize: "1.15rem", fontWeight: 700 }}>
            <span className="num">{fmtNum(s.leavesNeedsFollowup)}</span>
          </div>
          <div className="t-meta">من <span className="num">{fmtNum(s.leavesOpen)}</span> مفتوحة</div>
        </button>
        <Stat label="بنود تحت المراجعة"
              value={<span className="num">{fmtNum(s.warningsPending)}</span>}
              sub={`${fmtNum(s.warningsTotal)} إجمالي`} />
        <Stat label="إجمالي التوقفات"
              value={<span className="num">{fmtNum(s.leavesTotal)}</span>} />
        <span style={{ flex: 1 }} />
        {/* ⚠️ `null` = القناة مش شغّالة — والصفر كان هيكدب */}
        <div style={{ lineHeight: 1.7 }}>
          <div className="t-meta">طلبات المعلمين</div>
          <div style={{ fontSize: "1.05rem", fontWeight: 600 }}>
            {s.teacherRequests === null ? "— القناة مش شغّالة"
              : <span className="num">{fmtNum(s.teacherRequests)}</span>}
          </div>
          {s.teacherRequests === null ? (
            <div className="t-meta">بتوصل من تليجرام — التوكن لسه مش متحط</div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, sub }: { label: string; value: React.ReactNode; sub?: string }) {
  return (
    <div style={{ lineHeight: 1.7 }}>
      <div className="t-meta">{label}</div>
      <div style={{ fontSize: "1.15rem", fontWeight: 700 }}>{value}</div>
      {sub ? <div className="t-meta">{sub}</div> : null}
    </div>
  );
}

/* ══════════════════════════ التوقفات ══════════════════════════ */

type LState =
  | { kind: "loading" }
  | { kind: "ready"; rows: LeaveRow[]; meta: LeavesMeta }
  | { kind: "error"; messageAr: string }
  | { kind: "offline" };

function LeavesTab({ query, onClearSearch, onChanged }: {
  query: string; onClearSearch: () => void; onChanged: () => void;
}) {
  const [segment, setSegment] = useState<LeaveSegment>("needs_followup");
  const [page, setPage] = useState(1);
  const [state, setState] = useState<LState>({ kind: "loading" });
  const [editing, setEditing] = useState<LeaveRow | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => { setPage(1); }, [query, segment]);

  const load = useCallback(async () => {
    setState({ kind: "loading" });
    try {
      const { rows, meta } = await listSupervisionLeaves({
        segment, q: query || undefined, page, perPage: PER_PAGE });
      setState({ kind: "ready", rows, meta });
    } catch (err) {
      console.error("[supervision] تحميل التوقفات فشل", err);
      setState(err instanceof OfflineError
        ? { kind: "offline" }
        : { kind: "error", messageAr: errorMessageAr(err) });
    }
  }, [segment, query, page]);
  useEffect(() => { void load(); }, [load]);

  const rows = state.kind === "ready" ? state.rows : [];
  const meta = state.kind === "ready" ? state.meta : null;

  return (
    <>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12,
                    alignItems: "center" }}>
        {LEAVE_SEGMENTS.map((s) => (
          <button key={s.key} type="button"
                  className={`filt${segment === s.key ? " on" : ""}`}
                  onClick={() => setSegment(s.key)}>
            {s.chip}
            {meta ? <> · <span className="num">
              {fmtNum(meta.segmentCounts[s.key] ?? 0)}</span></> : null}
          </button>
        ))}
        {/* التعريف بييجي من الكتالوج — نص مكرر هنا معناه نسختان بتفترقا */}
        {LEAVE_SEGMENTS.filter((s) => s.key === segment && s.defAr).map((s) => (
          <span key={s.key} className="t-meta">
            «{s.chip}» = {s.defAr}
          </span>
        ))}
      </div>

      {toast ? (
        <div className="reg-card" style={{ padding: 10, marginTop: 10 }}
             role="status" aria-live="polite">
          {toast}{" "}
          <button type="button" className="filt" onClick={() => setToast(null)}>تمام</button>
        </div>
      ) : null}

      <div className="reg-card" style={{ marginTop: 12 }}>
        <div className="reg-head">
          <span className="sec-title">
            {meta ? <><span className="num">{fmtNum(meta.total)}</span> توقف</> : "التوقفات"}
            {query ? ` · بحث عن «${query}»` : ""}
          </span>
        </div>

        {state.kind === "loading" ? <LoadingState rows={5} /> : null}
        {state.kind === "offline" ? <OfflineState onRetry={() => void load()} /> : null}
        {state.kind === "error" ? (
          <ErrorState messageAr={state.messageAr} onRetry={() => void load()} />
        ) : null}
        {state.kind === "ready" && rows.length === 0 && query ? (
          <NoResultsState query={query} onClear={onClearSearch} />
        ) : null}
        {state.kind === "ready" && rows.length === 0 && !query
          && segment === "needs_followup" ? (
          <EmptyState title="مفيش توقف مستحق المتابعة 🌿"
                      hint="كل التوقفات المفتوحة ليها موعد متابعة لسه ما جاش"
                      action={{ label: "شوف المفتوحة", onClick: () => setSegment("open") }} />
        ) : null}
        {state.kind === "ready" && rows.length === 0 && !query
          && segment !== "needs_followup" ? (
          <EmptyState title="مفيش توقفات" hint="التوقفات بتتسجّل من سجل الطالب أو الأسرة" />
        ) : null}

        {rows.length > 0 ? (
          <div className="reg-scroll">
            <table className="tbl">
              <thead>
                <tr>
                  <th>مين</th><th>النوع</th><th>من</th><th>لغاية</th>
                  <th>المتابعة الجاية</th><th>السبب</th><th>الناقص</th><th />
                </tr>
              </thead>
              <tbody>
                {rows.map((l) => (
                  <tr key={l.id}>
                    <td>
                      <bdi>{l.subject_name_ar || <Dash />}</bdi>
                      <span className="t-meta"> · {SUBJ_AR[l.subject_type] || l.subject_type}</span>
                    </td>
                    <td className="t-desc">{LEAVE_TYPE_AR[l.type] || l.type}</td>
                    <td><DateAr v={l.start_date} /></td>
                    <td>
                      {l.end_date ? <DateAr v={l.end_date} />
                        : l.end_date_unknown ? <span className="t-meta">غير معروفة</span>
                        : <Dash />}
                    </td>
                    <td>
                      {l.next_check_at ? (
                        <>
                          <DateAr v={l.next_check_at} />
                          {l.followup_overdue ? (
                            <span className="src" title="الموعد عدّى"> فات</span>
                          ) : null}
                        </>
                      ) : <span className="t-meta">مفيش</span>}
                    </td>
                    <td className="t-desc"><bdi>{l.reason_ar || <Dash />}</bdi></td>
                    <td className="t-meta">{l.missing_ar || "—"}</td>
                    <td style={{ textAlign: "end" }}>
                      <button type="button" className="filt on"
                              onClick={() => setEditing(l)}>متابعة</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        {meta && meta.totalPages > 1 ? (
          <Pager page={meta.page} totalPages={meta.totalPages} total={meta.total}
                 onPage={setPage} />
        ) : null}
      </div>

      {editing ? (
        <LeaveDialog
          leave={editing}
          onClose={() => setEditing(null)}
          onDone={async (msg) => {
            setEditing(null); setToast(msg); await load(); onChanged();
          }}
        />
      ) : null}
    </>
  );
}

/**
 * ⚠️ **الحقول اللي السيرفر بيفرضها معروضة صراحةً**: التوقف اللي نهايته
 *    غير معروفة **لازم** يبقى ليه موعد متابعة، والإقفال **لازم** ليه تاريخ
 *    نهاية. الشاشة بتقول ده قبل الحفظ بدل ما الموظف يجرّب ويترفض.
 */
function LeaveDialog({ leave, onClose, onDone }: {
  leave: LeaveRow; onClose: () => void; onDone: (t: string) => void | Promise<void>;
}) {
  const [reason, setReason] = useState(leave.reason_ar ?? "");
  const [endDate, setEndDate] = useState(leave.end_date?.slice(0, 10) ?? "");
  const [unknown, setUnknown] = useState(leave.end_date_unknown);
  const [check, setCheck] = useState(leave.next_check_at?.slice(0, 10) ?? "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save(close: boolean) {
    setBusy(true); setErr(null);
    try {
      const patch: Record<string, unknown> = {};
      if (reason.trim() !== (leave.reason_ar ?? "")) patch["reason_ar"] = reason.trim();
      if (endDate) patch["end_date"] = new Date(`${endDate}T00:00:00Z`).toISOString();
      if (unknown !== leave.end_date_unknown) patch["end_date_unknown"] = unknown;
      if (check) patch["next_check_at"] = new Date(`${check}T00:00:00Z`).toISOString();
      if (close) patch["status"] = "ended";
      const { toastAr } = await patchLeave(leave.id, patch, leave.version);
      await onDone(toastAr);
    } catch (e) {
      setErr(errorMessageAr(e)); setBusy(false);
    }
  }

  const needsCheck = unknown && !check;
  const needsEnd = !endDate;

  return (
    <div role="dialog" aria-modal="true" aria-label="متابعة التوقف"
         className="modal-scrim"
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="reg-card" style={{ width: "min(520px, 100%)", padding: 16 }}>
        <div className="sec-title">متابعة التوقف</div>
        <div className="t-meta" style={{ lineHeight: 1.9 }}>
          <bdi>{leave.subject_name_ar || "—"}</bdi> ·{" "}
          {LEAVE_TYPE_AR[leave.type] || leave.type}
        </div>
        {leave.missing_ar ? (
          <div className="t-meta" style={{ marginTop: 6 }}>الناقص: {leave.missing_ar}</div>
        ) : null}

        <label style={{ display: "block", marginTop: 12 }}>
          <div className="t-meta">السبب</div>
          <input className="inp" style={{ width: "100%" }} value={reason}
                 onChange={(e) => setReason(e.target.value)}
                 placeholder="سبب التوقف زي ما ولي الأمر قاله" />
        </label>

        <label style={{ display: "block", marginTop: 10 }}>
          <div className="t-meta">تاريخ النهاية</div>
          <input className="inp" type="date" value={endDate} disabled={unknown}
                 onChange={(e) => setEndDate(e.target.value)} />
        </label>

        <label style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "center" }}>
          <input type="checkbox" checked={unknown}
                 onChange={(e) => { setUnknown(e.target.checked); if (e.target.checked) setEndDate(""); }} />
          <span className="t-meta">النهاية غير معروفة</span>
        </label>

        <label style={{ display: "block", marginTop: 10 }}>
          <div className="t-meta">
            موعد المتابعة الجاي
            {needsCheck ? <strong> — إجباري لأن النهاية غير معروفة</strong> : null}
          </div>
          <input className="inp" type="date" value={check}
                 onChange={(e) => setCheck(e.target.value)} />
        </label>

        {err ? (
          <div className="reg-card" style={{ padding: 10, marginTop: 10 }} role="alert">
            ⚠️ {err}
          </div>
        ) : null}

        <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
          <button type="button" className="filt on" disabled={busy || needsCheck}
                  onClick={() => void save(false)}>احفظ</button>
          <button type="button" className="filt" disabled={busy || needsEnd}
                  onClick={() => void save(true)}
                  title={needsEnd ? "الإقفال محتاج تاريخ نهاية" : ""}>
            اقفل التوقف
          </button>
          <span style={{ flex: 1 }} />
          <button type="button" className="filt" disabled={busy}
                  onClick={onClose}>إلغاء</button>
        </div>
        {needsEnd ? (
          <div className="t-meta" style={{ marginTop: 8 }}>
            الإقفال محتاج تاريخ نهاية — من غيره مفيش حساب مدة ولا استئناف ساعات.
          </div>
        ) : null}
      </div>
    </div>
  );
}

/* ══════════════════════════ المخالفات ══════════════════════════ */

function WarningsTab({ onChanged, reloadKey }: { onChanged: () => void; reloadKey: number }) {
  const [rows, setRows] = useState<WarningRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const { rows: r } = await listWarnings({ perPage: PER_PAGE });
      setRows(r); setErr(null);
    } catch (e) {
      console.error("[supervision] تحميل البنود فشل", e);
      setErr(errorMessageAr(e));
    }
  }, []);
  useEffect(() => { void load(); }, [load, reloadKey]);

  async function decide(w: WarningRow, approve: boolean) {
    // ⚠️ الرفض بلا سبب بيرجّع البند زي ما هو — فالسبب إجباري قدّام الموظف
    //    برضه، مش في السيرفر بس.
    let note = "";
    if (!approve) {
      note = window.prompt(
        "سبب الرفض (إجباري — البند المرفوض بلا سبب بيرجع زي ما هو):") ?? "";
      if (!note.trim()) { setToast("الرفض محتاج سبب."); return; }
    }
    try {
      const { toastAr } = await decideWarning(w.id, approve, w.version, note);
      setToast(toastAr); await load(); onChanged();
    } catch (e) { setToast(errorMessageAr(e)); }
  }

  return (
    <>
      <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
        <button type="button" className="filt on" onClick={() => setAdding(true)}>
          + سجّل بند
        </button>
        <span className="t-meta" style={{ alignSelf: "center" }}>
          اللي بيرصد البند مش اللي بيعتمده — الاعتماد صلاحية تانية
        </span>
      </div>

      {toast ? (
        <div className="reg-card" style={{ padding: 10, marginTop: 10 }} role="status">
          {toast} <button type="button" className="filt"
                          onClick={() => setToast(null)}>تمام</button>
        </div>
      ) : null}

      <div className="reg-card" style={{ marginTop: 12 }}>
        <div className="reg-head"><span className="sec-title">المخالفات والمكافآت</span></div>
        {err ? <ErrorState messageAr={err} onRetry={() => void load()} /> : null}
        {!rows && !err ? <LoadingState rows={4} /> : null}
        {rows && rows.length === 0 ? (
          <EmptyState title="لسه مفيش بنود"
                      hint="البند بيتسجّل باسم اللي رصده وبيروح للمدير يعتمده" />
        ) : null}
        {rows && rows.length > 0 ? (
          <div className="reg-scroll">
            <table className="tbl">
              <thead>
                <tr>
                  <th>المعلم</th><th>النوع</th><th>السبب</th><th>رصدها</th>
                  <th>الحالة</th><th>قرّر</th><th />
                </tr>
              </thead>
              <tbody>
                {rows.map((w) => (
                  <tr key={w.id}>
                    <td><bdi>{w.subject_name_ar || <Dash />}</bdi></td>
                    <td className="t-desc">{w.kind_ar}</td>
                    <td className="t-desc"><bdi>{w.reason_ar || <Dash />}</bdi></td>
                    <td className="t-desc"><bdi>{w.recorded_by_name_ar || <Dash />}</bdi></td>
                    <td className="t-desc">{w.status_ar}</td>
                    <td className="t-desc">
                      <bdi>{w.decided_by_name_ar || <Dash />}</bdi>
                      {w.decision_note_ar ? (
                        <div className="t-meta"><bdi>{w.decision_note_ar}</bdi></div>
                      ) : null}
                    </td>
                    <td style={{ textAlign: "end", whiteSpace: "nowrap" }}>
                      {w.status === "pending_review" ? (
                        <>
                          <button type="button" className="filt on"
                                  onClick={() => void decide(w, true)}>اعتمد</button>{" "}
                          <button type="button" className="filt"
                                  onClick={() => void decide(w, false)}>ارفض</button>
                        </>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>

      {adding ? (
        <WarningDialog onClose={() => setAdding(false)}
                       onDone={async (m) => { setAdding(false); setToast(m);
                                              await load(); onChanged(); }} />
      ) : null}
    </>
  );
}

function WarningDialog({ onClose, onDone }: {
  onClose: () => void; onDone: (t: string) => void | Promise<void>;
}) {
  const [term, setTerm] = useState("");
  const [results, setResults] = useState<{ id: string; name_ar: string }[]>([]);
  const [picked, setPicked] = useState<{ id: string; name_ar: string } | null>(null);
  const [kind, setKind] = useState("penalty");
  const [reason, setReason] = useState("");
  const [rule, setRule] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const deb = useRef<number | null>(null);

  useEffect(() => {
    const t = term.trim();
    if (deb.current) window.clearTimeout(deb.current);
    if (t.length < MIN_SEARCH) { setResults([]); return; }
    deb.current = window.setTimeout(async () => {
      try { setResults(await searchTeachers(t)); setErr(null); }
      catch (e) { console.error("[supervision] بحث المعلمين فشل", e); setErr(errorMessageAr(e)); }
    }, DEBOUNCE_MS);
    return () => { if (deb.current) window.clearTimeout(deb.current); };
  }, [term]);

  async function submit() {
    if (!picked || !reason.trim()) return;
    setBusy(true); setErr(null);
    try {
      const { toastAr } = await proposeWarning({
        subject_ref: picked.id, kind, reason_ar: reason.trim(),
        ...(rule.trim() ? { rule_ref: rule.trim() } : {}),
      });
      await onDone(toastAr);
    } catch (e) { setErr(errorMessageAr(e)); setBusy(false); }
  }

  return (
    <div role="dialog" aria-modal="true" aria-label="تسجيل بند"
         className="modal-scrim"
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="reg-card" style={{ width: "min(520px, 100%)", padding: 16 }}>
        <div className="sec-title">سجّل بند إشراف</div>
        <div className="t-meta" style={{ lineHeight: 1.9 }}>
          البند بيتسجّل <strong>باسمك</strong> وبيفضل تحت المراجعة لحد ما المدير يقرر.
        </div>

        {picked ? (
          <div style={{ marginTop: 10 }}>
            <bdi style={{ fontWeight: 600 }}>{picked.name_ar}</bdi>{" "}
            <button type="button" className="filt"
                    onClick={() => { setPicked(null); setTerm(""); }}>غيّر</button>
          </div>
        ) : (
          <>
            <input className="inp" autoFocus value={term} style={{ width: "100%", marginTop: 10 }}
                   onChange={(e) => setTerm(e.target.value)}
                   placeholder="ابحث باسم المعلم…" aria-label="ابحث عن المعلم" />
            <div style={{ maxHeight: 180, overflowY: "auto", marginTop: 8 }}>
              {term.trim().length < MIN_SEARCH ? (
                <div className="t-meta" style={{ padding: 8 }}>اكتب حرفين على الأقل</div>
              ) : results.length === 0 ? (
                <div className="t-meta" style={{ padding: 8 }}>مفيش معلم بالاسم ده</div>
              ) : results.map((t) => (
                <button key={t.id} type="button" className="filt"
                        style={{ display: "block", width: "100%", textAlign: "start",
                                 marginBottom: 6 }}
                        onClick={() => setPicked(t)}>
                  <bdi>{t.name_ar}</bdi>
                </button>
              ))}
            </div>
          </>
        )}

        <label style={{ display: "block", marginTop: 10 }}>
          <div className="t-meta">النوع</div>
          <select className="filt" value={kind} onChange={(e) => setKind(e.target.value)}>
            <option value="penalty">مخالفة</option>
            <option value="reward">مكافأة</option>
            <option value="formal_warning">إنذار</option>
          </select>
        </label>

        <label style={{ display: "block", marginTop: 10 }}>
          <div className="t-meta">السبب — إجباري</div>
          <input className="inp" style={{ width: "100%" }} value={reason}
                 onChange={(e) => setReason(e.target.value)}
                 placeholder="البند بلا سبب بيتحوّل لخلاف" />
        </label>

        <label style={{ display: "block", marginTop: 10 }}>
          <div className="t-meta">بند اللائحة (اختياري)</div>
          <input className="inp" style={{ width: "100%" }} value={rule}
                 onChange={(e) => setRule(e.target.value)} />
        </label>

        {err ? (
          <div className="reg-card" style={{ padding: 10, marginTop: 10 }} role="alert">
            ⚠️ {err}
          </div>
        ) : null}

        <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
          <button type="button" className="filt on" disabled={busy || !picked || !reason.trim()}
                  onClick={() => void submit()}>سجّل</button>
          <span style={{ flex: 1 }} />
          <button type="button" className="filt" disabled={busy} onClick={onClose}>إلغاء</button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════ طلبات المعلمين ══════════════════════════ */

/**
 * ⛔ **حالة فاضية صادقة، مش شاشة بيضا.** الطلبات بتوصل من تليجرام والتوكن
 *    لسه مش متحط، فالشاشة بتقول ده صراحةً بدل ما تعرض صفرًا يخلّي المشرفة
 *    تفتكر إن مفيش طلبات وتستنى.
 */
function RequestsTab({ s }: { s: SupervisionSummary | null }) {
  if (s && s.teacherRequests !== null) {
    return (
      <div className="reg-card" style={{ marginTop: 12 }}>
        <EmptyState title="مفيش طلبات دلوقتي"
                    hint="الطلب بيوصل من تليجرام ويتسجّل باسم مشرفه" />
      </div>
    );
  }
  return (
    <div className="reg-card" style={{ marginTop: 12, padding: 20, lineHeight: 2 }}>
      <div className="sec-title">القناة مش شغّالة لسه</div>
      <p className="t-meta">
        طلبات المعلمين (إجازة · تعويض حصة · تعديل إتاحة) بتوصل من <strong>تليجرام</strong>،
        والبوت لسه مالوش توكن — فمفيش طلب بيوصل السيستم أصلًا.
      </p>
      <p className="t-meta">
        ⛔ وعشان كده الشاشة بتقول كده صراحةً بدل ما تعرض <span className="num">0</span>{" "}
        وتخلّيك تفتكر إن مفيش طلبات وتستنى.
      </p>
      <p className="t-meta">
        <strong>اللي محتاجينه:</strong> توكن بوت تليجرام يتحط في إعدادات السيستم.
        ولحد ما يجي، سجّلي الطلب اللي بيوصلك على واتساب كـ<strong>توقف</strong> من
        تبويب «التوقفات» لو إجازة، أو كـ<strong>بند</strong> لو مخالفة.
      </p>
    </div>
  );
}

/* ══════════════════════════ الترقيم ══════════════════════════ */

function Pager({ page, totalPages, total, onPage }: {
  page: number; totalPages: number; total: number; onPage: (p: number) => void;
}) {
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "center", padding: "10px 14px" }}>
      <button type="button" className="filt" disabled={page <= 1}
              onClick={() => onPage(page - 1)}>السابق</button>
      <span className="t-meta">
        صفحة <span className="num">{fmtNum(page)}</span> من{" "}
        <span className="num">{fmtNum(totalPages)}</span>
        {" · "}<span className="num">{fmtNum(total)}</span> سجل
      </span>
      <button type="button" className="filt" disabled={page >= totalPages}
              onClick={() => onPage(page + 1)}>التالي</button>
    </div>
  );
}
